/**
 * Deterministic NILM classification metrics — a faithful client-side port of
 * `researchharness/analysis/classification.py` (`compute_classification_metrics`).
 *
 * NILM appliance-type disaggregation IS multi-class classification, so the workbench
 * scores it exactly the way the server-side analyzer does: it joins predictions to the
 * benchmark's ground-truth labels on `sample_id` (restricted to the benchmark's sample
 * set), builds a confusion matrix, and derives precision / recall / F1. The math is
 * deterministic and identical to the Python analyzer — the browser panel is a preview of
 * the same figures the sealed `evidence.v1` run certifies, never a second source of truth.
 *
 * It certifies METRICS, not the model: coverage gaps are reported as counts + flags, never
 * folded into the confusion matrix (folding a missing prediction into recall would require
 * *assuming* it is wrong — an interpretation this analyzer refuses to make).
 */

/** The analyzer's own limitation notes, surfaced VERBATIM in the UI (never softened). */
export const NILM_ANALYZER_NOTES =
  'Reports classification concordance against the pinned benchmark labels (join on ' +
  "sample_id, restricted to the benchmark's sample set); makes no claim about model " +
  'quality, generalization, training validity, or fitness for purpose; cannot detect ' +
  'train/test leakage. The model run that produced the predictions is researcher-reported; ' +
  'only the confusion matrix and the figures of merit derived from it are tool-computed.';

function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

function safeRatio(numerator: number, denominator: number): number {
  return denominator === 0 ? 0 : numerator / denominator;
}

function mean(values: number[]): number {
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
}

export type PerClassMetrics = {
  precision: number;
  recall: number;
  f1: number;
  support: number;
  tp: number;
  fp: number;
  fn: number;
};

export type ClassificationMetrics = {
  labels: string[];
  confusion: Record<string, Record<string, number>>;
  perClass: Record<string, PerClassMetrics>;
  accuracy: number;
  balancedAccuracy: number;
  macroPrecision: number;
  macroRecall: number;
  macroF1: number;
  weightedF1: number;
  nEvaluated: number;
  nClasses: number;
  nPredictions: number;
  nBenchmarkSamples: number;
  nCorrect: number;
  missingPredictionIds: string[];
  extraPredictionIds: string[];
  flags: string[];
};

/**
 * Compute classification figures of merit from a predictions map and a truth map, keyed by
 * `sample_id`. Mirrors the Python analyzer exactly (evaluated intersection only; coverage
 * gaps reported as flags, never folded into the matrix).
 */
export function computeClassificationMetrics(
  predMap: Map<string, string>,
  truthMap: Map<string, string>,
  { imbalanceRatio = 10, tinySupport = 5 }: { imbalanceRatio?: number; tinySupport?: number } = {},
): ClassificationMetrics {
  const benchmarkIds = new Set(truthMap.keys());
  const predictionIds = new Set(predMap.keys());
  const evaluatedIds = [...benchmarkIds].filter((id) => predictionIds.has(id)).sort();
  const missingPredictionIds = [...benchmarkIds].filter((id) => !predictionIds.has(id)).sort();
  const extraPredictionIds = [...predictionIds].filter((id) => !benchmarkIds.has(id)).sort();

  const pairs: Array<[string, string]> = evaluatedIds.map((id) => [
    truthMap.get(id) as string,
    predMap.get(id) as string,
  ]);
  const truthLabels = new Set(pairs.map(([t]) => t));
  const predLabels = new Set(pairs.map(([, p]) => p));
  const labelSet = [...new Set([...truthLabels, ...predLabels])].sort();

  const confusion: Record<string, Record<string, number>> = {};
  for (const t of labelSet) {
    confusion[t] = {};
    for (const p of labelSet) confusion[t][p] = 0;
  }
  for (const [t, p] of pairs) confusion[t][p] += 1;

  const perClass: Record<string, PerClassMetrics> = {};
  for (const c of labelSet) {
    const tp = confusion[c][c];
    const fp = labelSet.reduce((acc, t) => acc + (t !== c ? confusion[t][c] : 0), 0);
    const fn = labelSet.reduce((acc, p) => acc + (p !== c ? confusion[c][p] : 0), 0);
    const support = labelSet.reduce((acc, p) => acc + confusion[c][p], 0);
    const precision = safeRatio(tp, tp + fp);
    const recall = safeRatio(tp, tp + fn);
    const f1 = safeRatio(2 * precision * recall, precision + recall);
    perClass[c] = {
      precision: round6(precision),
      recall: round6(recall),
      f1: round6(f1),
      support,
      tp,
      fp,
      fn,
    };
  }

  const nEvaluated = pairs.length;
  const nCorrect = labelSet.reduce((acc, c) => acc + confusion[c][c], 0);
  const accuracy = safeRatio(nCorrect, nEvaluated);
  const supported = labelSet.filter((c) => perClass[c].support > 0);
  const balancedAccuracy = supported.length ? mean(supported.map((c) => perClass[c].recall)) : 0;

  const macro = (key: 'precision' | 'recall' | 'f1') =>
    labelSet.length ? round6(mean(labelSet.map((c) => perClass[c][key]))) : 0;
  const weighted = (key: 'precision' | 'recall' | 'f1') => {
    if (nEvaluated <= 0) return 0;
    const total = labelSet.reduce((acc, c) => acc + perClass[c][key] * perClass[c].support, 0);
    return round6(total / nEvaluated);
  };

  const flags: string[] = [];
  if (nEvaluated === 0) {
    flags.push('no_overlapping_samples');
  } else {
    if (predLabels.size === 1 && truthLabels.size > 1) flags.push('degenerate_single_prediction_class');
    const supports = labelSet.map((c) => perClass[c].support).filter((s) => s > 0);
    if (supports.length && Math.max(...supports) / Math.min(...supports) > imbalanceRatio) {
      flags.push('class_imbalance');
    }
    if (labelSet.some((c) => perClass[c].support > 0 && perClass[c].support < tinySupport)) {
      flags.push('tiny_support_classes');
    }
    if (![...predLabels].every((p) => truthLabels.has(p))) flags.push('label_set_mismatch');
    if (missingPredictionIds.length) flags.push('coverage_gap_predictions_missing');
    if (extraPredictionIds.length) flags.push('coverage_gap_extra_predictions');
  }

  return {
    labels: labelSet,
    confusion,
    perClass,
    accuracy: round6(accuracy),
    balancedAccuracy: round6(balancedAccuracy),
    macroPrecision: macro('precision'),
    macroRecall: macro('recall'),
    macroF1: macro('f1'),
    weightedF1: weighted('f1'),
    nEvaluated,
    nClasses: labelSet.length,
    nPredictions: predMap.size,
    nBenchmarkSamples: truthMap.size,
    nCorrect,
    missingPredictionIds,
    extraPredictionIds,
    flags,
  };
}

export type ParsedCsv = { map: Map<string, string>; rowCount: number };

/**
 * Parse a two-column-ish CSV (`sample_id` + a label column) into a `sample_id -> label`
 * map. Extra columns (e.g. a prediction `score`) are ignored. Throws a readable error the
 * panel can surface. Metadata only — never the raw feature rows.
 */
export function parseLabelCsv(text: string, idCol: string, labelCol: string): ParsedCsv {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length < 2) throw new Error('CSV needs a header row and at least one data row.');
  const header = lines[0].split(',').map((cell) => cell.trim());
  const idIndex = header.indexOf(idCol);
  const labelIndex = header.indexOf(labelCol);
  if (idIndex === -1) throw new Error(`missing column "${idCol}" (have: ${header.join(', ')})`);
  if (labelIndex === -1) throw new Error(`missing column "${labelCol}" (have: ${header.join(', ')})`);

  const map = new Map<string, string>();
  for (let i = 1; i < lines.length; i += 1) {
    const cells = lines[i].split(',').map((cell) => cell.trim());
    const sampleId = cells[idIndex] ?? '';
    const label = cells[labelIndex] ?? '';
    if (!sampleId || !label) throw new Error(`row ${i + 1}: empty ${idCol} or ${labelCol}.`);
    if (map.has(sampleId)) throw new Error(`duplicate ${idCol} "${sampleId}".`);
    map.set(sampleId, label);
  }
  if (map.size === 0) throw new Error('no rows found.');
  return { map, rowCount: map.size };
}
